import clsx from 'clsx';

import Stock from '@/cataloguePages/productPage/components/Stock';
import Link from 'next/link';
import { Button } from '@fagmobler/ui';
import { CubeIcon, ShoppingCartIcon } from '@heroicons/react/24/outline';
import { Alert, StoreIcon } from '@fagmobler/ui';
import { ServerProduct } from '@/lib/janitsjar/Product';
import {
  FagmoblerAlgoliaProduct,
  FagmoblerPublicOmniumProduct,
} from '@fagmobler/types';
import { useNavigation } from '@/contexts/navigation';
import { useStores } from '@/components/store';
import AddToCart from '@/components/AddToCart/AddToCart';
import { useCallback, useEffect, useState } from 'react';
import { trackEvent } from '@/lib/analytics';

export type AddToCartBlockProps = {
  product: ServerProduct;
  algoliaProduct: FagmoblerAlgoliaProduct;
  omniumProduct: FagmoblerPublicOmniumProduct;
};

export function AddToCartBlock({
  product,
  algoliaProduct,
  omniumProduct,
}: AddToCartBlockProps) {
  const [listenForStoreChange, setListenForStoreChange] = useState(false);
  const { setClickAndCollect, setShowStoreStock } = useNavigation();
  const { current: currentStoreQuery } = useStores();

  const currentStore = currentStoreQuery.data?.store;
  const builder = product.external3DLink ?? null;
  const currentStoreStock =
    product.stock?.find((s) => s.storeId === currentStore?.id) || null;
  const isInStock = currentStoreStock ? currentStoreStock.stock > 0 : false;
  const notAvailable = !currentStoreStock?.availableToOrder && !isInStock;

  const inventory =
    product.stock?.reduce((acc, stockItem) => {
      return {
        ...acc,
        [product.sku]: {
          [stockItem.storeId]: {
            display: stockItem.display,
            stock: stockItem.stock,
          },
        },
      };
    }, {}) || {};

  const openClickAndCollect = useCallback(() => {
    if (currentStore) {
      setClickAndCollect({
        product: omniumProduct,
        visible: true,
        quantity: 1,
        stock: currentStoreStock
          ? {
              display: currentStoreStock.display,
              stock: currentStoreStock.stock,
            }
          : undefined,
        store: currentStore,
      });
      trackEvent(
        'clickandcollect_checkout',
        {
          currency: 'NOK',
          value: product.price.discountedAmount || undefined,
          items: [
            {
              item_id: product.sku,
              item_name: product.posterName,
              price: product.price.discountedAmount || undefined,
              quantity: 1,
            },
          ],
        },
        currentStore
      );
    }
  }, [product, currentStore, currentStoreStock, setClickAndCollect]);

  useEffect(() => {
    if (listenForStoreChange && currentStore) {
      setListenForStoreChange(false);
      openClickAndCollect();
    }
  }, [listenForStoreChange, currentStore, openClickAndCollect]);

  return (
    <Stock
      product={product}
      omniumProduct={omniumProduct}
      algoliaProduct={algoliaProduct}
      inventory={inventory || {}}
      changeStore={() => {
        setShowStoreStock({
          visible: true,
          inventory: inventory,
          selectedProduct: omniumProduct,
        });
      }}
      notAvailable={notAvailable}
    >
      {builder && (
        <Link href={builder as string} legacyBehavior passHref>
          <Button
            className={clsx('w-full', 'justify-center', 'mb-2')}
            variant="outline"
          >
            <CubeIcon className={clsx('w-4', 'h-4', 'mr-2')} /> Bygg produktet i
            3D
          </Button>
        </Link>
      )}
      <div className="grid grid-cols-2 gap-4">
        <div className="w-full">
          <Button
            className="text-center justify-center w-full"
            disabled={!!currentStore && !isInStock}
            variant="outline"
            title={
              isInStock
                ? 'Klikk og hent'
                : 'Ikke på lager. Klikk og hent ikke tilgjengelig '
            }
            onClick={() => {
              if (currentStore) {
                openClickAndCollect();
              }
              if (!currentStore) {
                setShowStoreStock({
                  title: 'Klikk og hent',
                  visible: true,
                  selectedProduct: omniumProduct,
                  inventory: inventory || null,
                });

                setListenForStoreChange(true);
              }
            }}
          >
            <StoreIcon className="h-4 w-4 mr-2" />
            Klikk og hent
          </Button>
          <p className="body-xs mt-2">
            Betal i butikk. Vi sender deg en SMS når varen er klar til henting.
          </p>
        </div>
        <AddToCart
          product={product}
          algoliaProduct={algoliaProduct}
          disabled={notAvailable}
        >
          <ShoppingCartIcon className={clsx('w-4', 'h-4', 'mr-2')} /> Legg i
          handlekurv
        </AddToCart>
      </div>
      {notAvailable && (
        <Alert className="mt-4" type="error">
          Tomt på lager.
        </Alert>
      )}
    </Stock>
  );
}

export default AddToCartBlock;
